import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  input,
  Input,
  model,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  viewChild,
} from '@angular/core';

import { TableConfig } from '@compeople-shared/design-system';
import { ColumnSettings } from '@compeople-shared/design-system/dist/types/components';
import { DxTable } from '@compeople-shared/design-system-angular';
import { UntilDestroy } from '@ngneat/until-destroy';

import { PageInfo } from '@api';

import { SpaColTplComponent } from './spa-col-template/spa-col-tpl.component';

@UntilDestroy()
@Component({
  selector: 'spa-table',
  templateUrl: './spa-table.component.html',
  styleUrls: ['./spa-table.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpaTableComponent<T> implements OnChanges {
  @Input() columns: Column<T>[] = [];

  totalPages = 1;
  totalElements = 0;

  currentPage = model<number>(1);

  @Input() pageSize = 7;

  data: Row<T>[] = [];

  paginated = false;

  private _rows: Row<T>[] | PaginatedResponse<Row<T>> | undefined;
  get rows(): Row<T>[] | PaginatedResponse<Row<T>> | undefined {
    return this._rows;
  }
  @Input() set rows(rows: Row<T>[] | PaginatedResponse<Row<T>>) {
    this._rows = rows;

    if (rows && 'elements' in rows) {
      this.paginated = true;
      this.data = rows.elements;
      this.totalPages = rows.page.totalPages;
      this.totalElements = rows.page.totalElements;
      this.currentPage.set(rows.page.number + 1);
    } else {
      // unpaginated table
      this.data = rows as Row<T>[];
      this.totalElements = this.data?.length;
    }
  }

  @Input() rowId: keyof T | undefined;
  readonly compareRows = input<((first: Row<T>, second: Row<T>) => boolean) | undefined>();
  @Input() showDetailIcon = false;

  private _selectedRow: Row<T> | undefined;
  get selectedRow(): Row<T> | undefined {
    return this._selectedRow;
  }
  @Input() set selectedRow(row: Row<T>) {
    this._selectedRow = row;
  }

  @Input() deleteDisabled?: (row: Row<T>) => boolean;

  @Input() loading = false;

  @Input() noDataLabel = 'No data';

  @Output() selectedRowChange: EventEmitter<Row<T>> = new EventEmitter<Row<T>>();
  @Output() rowDelete: EventEmitter<Row<T>> = new EventEmitter<Row<T>>();

  @Output() paginate = new EventEmitter<Pagination>();

  hideDeleteIcon = input<boolean>(false);
  hideSelectedRow = input<boolean>(false);
  deleteIcon = input<string>('delete_outline');
  dxPageSize = input<number | undefined>(undefined);
  dxPageSizes = input<number[] | undefined>(undefined);
  dxTable = viewChild(DxTable);

  @ContentChildren(SpaColTplComponent) columnTemplates: QueryList<SpaColTplComponent> | undefined;

  @ContentChildren(TemplateRef) tpls: QueryList<TemplateRef<unknown>> | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentPage'] && this.dxTable()) {
      this.dxTable()!.setState({ page: this.currentPage() });
    }
  }

  getTemplate(key: string | number | symbol) {
    return this.columnTemplates?.find((t) => t.columnKey === key);
  }

  areSame(first: Row<T>, second: Row<T>): boolean {
    if (!first || !second) {
      return false;
    }
    const compareRows = this.compareRows();
    if (compareRows) {
      return compareRows(first, second);
    }
    return this.rowId !== undefined && first.element?.[this.rowId] === second.element?.[this.rowId];
  }

  onRowSelect(row: Row<T>) {
    if (this.selectedRowChange.observed && !this.hideSelectedRow()) {
      this.selectedRow = row;
      this.selectedRowChange.emit(this.selectedRow);
    }
  }

  onRowDelete(row: Row<T>) {
    this.rowDelete.emit(row);
  }

  cellValue(row: Row<T>, column: Column<T>) {
    if (column.value) {
      return column.value(row);
    }

    return row.element?.[column.key as keyof T];
  }

  pageChanged(e: CustomEvent) {
    this.currentPage.set(e.detail);
    this.loadData(this.currentPage() - 1, this.pageSize);
  }

  loadData(page: number, pageSize: number) {
    this.paginate.emit({ page, pageSize });
  }

  translationKey(column: Column<T>) {
    if (column.hideHeader) {
      return null;
    }
    if (column.translationKey) {
      return column.translationKey;
    }
    return column.key ? ['label.', column.key].join('') : null;
  }

  handleTableStateChanged(event: CustomEvent<TableConfig>) {
    this.loadData(event.detail.page - 1, event.detail.pageSize);
  }
}

export type Row<T> = Partial<{
  element: T;
  updated: boolean;
  created: boolean;
}>;

export interface Column<T> {
  key: string | keyof T;
  translationKey?: string;
  hideHeader?: boolean;
  value?: (row: Row<T>) => unknown;
  settings?: Partial<ColumnSettings>;
  template?: TemplateRef<unknown>;
  template$?: QueryList<TemplateRef<unknown>>;
}

export interface Pagination {
  page: number;
  pageSize: number;
  sort?: string;
}

export interface PaginatedResponse<T> {
  page: PageInfo;
  elements: T[];
}

export function wrapElements<T>(paginatedResponse: PaginatedResponse<T>) {
  const wrapped: PaginatedResponse<Row<T>> = {
    ...paginatedResponse,
    elements: paginatedResponse.elements?.map((elemt) => wrap(elemt)),
  };
  return wrapped;
}

export function wrap<T>(element: T, updated?: boolean, created?: boolean): Row<T> {
  return (
    element &&
    ({
      element,
      updated,
      created,
    } as Row<T>)
  );
}

export function unwrap<T>(row: Row<T>): T | undefined {
  return row && row.element;
}
